import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { HubPage } from "../components/HubPage";
import StreakRecoveryCard from "../components/StreakRecoveryCard";
import { toast } from "sonner";
import {
  Target, BarChart3, Award, Activity, Inbox, LogOut, User, Lock, ShieldCheck,
  Save, Loader2, Copy, ShieldOff, QrCode, ChevronDown, ChevronUp,
} from "lucide-react";
import { COUNTRIES, CALLING_CODES } from "../lib/locations";
import { useLanguage } from "../i18n/LanguageContext";
import { goToDeleteAccount } from "@/lib/hifzScope";

const AGE_GROUPS = [
  { value: "under_13", label: "Under 13" },
  { value: "13_17", label: "13–17" },
  { value: "18_24", label: "18–24" },
  { value: "25_40", label: "25–40" },
  { value: "41_plus", label: "41+" },
];
const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

/* Splits an existing "+<code> <local number>" string into its parts so the
   code dropdown can be pre-selected on load. Longest-code-first so e.g.
   "+212…" isn't mismatched against "+2…" */
function splitWhatsapp(existing) {
  const codesByLength = [...CALLING_CODES].sort((a, b) => b.code.length - a.code.length);
  const trimmed = (existing || "").trim();
  for (const c of codesByLength) {
    if (trimmed.startsWith(c.code)) {
      return { code: c.code, local: trimmed.slice(c.code.length).trim() };
    }
  }
  return { code: "+1", local: trimmed };
}

function ProfileInfoForm() {
  const { tx, t } = useLanguage();
  const { user, setUser } = useAuth();
  const initialWhatsapp = splitWhatsapp(user?.whatsapp_number);
  const [form, setForm] = useState({
    name: user?.name || "",
    gender: user?.gender || "",
    city: user?.city || "",
    country: user?.country || "",
    age_group: user?.age_group || "",
    whatsapp_code: initialWhatsapp.code,
    whatsapp_local: initialWhatsapp.local,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { whatsapp_code, whatsapp_local, ...rest } = form;
      const payload = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== ""));
      if (whatsapp_local.trim()) payload.whatsapp_number = `${whatsapp_code} ${whatsapp_local.trim()}`;
      const r = await api.patch("/users/me/profile", payload);
      setUser(r.data);
      toast.success(t("profile.profileUpdated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.couldnTSaveProfile"));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="hifz-card space-y-4" data-testid="profile-info-form">
      <h2 className="font-display font-semibold text-lg text-forest-700 inline-flex items-center gap-2">
        <User className="h-4.5 w-4.5" /> {t("profile.yourInformation")}
      </h2>
      <div className="grid gap-3">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t("profile.name")}</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="profile-name-input" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t("profile.gender")}</label>
          <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
            className="mt-1 w-full rounded-xl border border-sand-300 px-3 py-2 text-sm bg-white" data-testid="profile-gender-select">
            <option value="">{t("profile.select")}</option>
            {GENDERS.map((g) => <option key={g.value} value={g.value}>{tx(g.label)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t("profile.city")}</label>
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="mt-1 w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="profile-city-input" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t("profile.country")}</label>
          <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="mt-1 w-full rounded-xl border border-sand-300 px-3 py-2 text-sm bg-white" data-testid="profile-country-select">
            <option value="">{t("profile.select")}</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t("profile.ageGroup")}</label>
          <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })}
            className="mt-1 w-full rounded-xl border border-sand-300 px-3 py-2 text-sm bg-white" data-testid="profile-age-group-select">
            <option value="">{t("profile.select")}</option>
            {AGE_GROUPS.map((a) => <option key={a.value} value={a.value}>{tx(a.label)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t("profile.whatsappNumber")}</label>
          <div className="mt-1 flex gap-2">
            <select value={form.whatsapp_code} onChange={(e) => setForm({ ...form, whatsapp_code: e.target.value })}
              className="w-24 rounded-xl border border-sand-300 px-2 py-2 text-sm bg-white" data-testid="profile-whatsapp-code-select">
              {CALLING_CODES.map((c) => <option key={`${c.country}-${c.code}`} value={c.code}>{c.code}</option>)}
            </select>
            <input value={form.whatsapp_local} onChange={(e) => setForm({ ...form, whatsapp_local: e.target.value })}
              placeholder="555 123 4567"
              className="flex-1 rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="profile-whatsapp-input" />
          </div>
        </div>
      </div>
      <button disabled={saving} type="submit" className="btn-primary text-sm disabled:opacity-60" data-testid="profile-info-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? t("profile.saving") : t("profile.saveChanges")}
      </button>
    </form>
  );
}

function ChangePasswordForm() {
  const { t } = useLanguage();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error(t("profile.newPasswordMustBeAt"));
    if (next !== confirm) return toast.error(t("profile.newPasswordsDonTMatch"));
    setSaving(true);
    try {
      await api.post("/auth/password/change", { current_password: current, new_password: next });
      toast.success(t("profile.passwordChanged"));
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.couldnTChangePassword"));
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="hifz-card space-y-4" data-testid="change-password-form">
      <h2 className="font-display font-semibold text-lg text-forest-700 inline-flex items-center gap-2">
        <Lock className="h-4.5 w-4.5" /> {t("profile.changePassword")}
      </h2>
      <div className="grid gap-3">
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
          placeholder={t("profile.currentPassword")} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="password-current-input" />
        <input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
          placeholder={t("profile.newPassword")} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="password-new-input" />
        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder={t("profile.confirmNewPassword")} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="password-confirm-input" />
      </div>
      <button disabled={saving} type="submit" className="btn-primary text-sm disabled:opacity-60" data-testid="password-change-submit">
        {saving ? t("profile.updating") : t("profile.updatePassword")}
      </button>
    </form>
  );
}

function MfaSection() {
  const { t } = useLanguage();
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(null); // {secret, qr_code}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/setup");
      setSetup(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.couldnTStartMfaSetup"));
    } finally { setBusy(false); }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify-setup", { code });
      toast.success(t("profile.mfaEnabledYourAccountIs"));
      setSetup(null); setCode("");
      setUser((u) => ({ ...u, mfa_enabled: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.invalidCode"));
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePassword });
      toast.success(t("profile.mfaDisabled"));
      setUser((u) => ({ ...u, mfa_enabled: false }));
      setShowDisable(false); setDisablePassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("profile.couldnTDisableMfa"));
    } finally { setBusy(false); }
  };

  return (
    <div className="hifz-card space-y-4" data-testid="mfa-section">
      <h2 className="font-display font-semibold text-lg text-forest-700 inline-flex items-center gap-2">
        <ShieldCheck className="h-4.5 w-4.5" /> {t("profile.twoFactorAuthentication")}
      </h2>
      <p className="text-sm text-muted-foreground">
        {t("profile.protectYourAccountWithGoogle")}
      </p>

      {user?.mfa_enabled ? (
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 status-pill status-memorized" data-testid="mfa-enabled-badge">
            <ShieldCheck className="h-3.5 w-3.5" /> {t("profile.enabled")}
          </div>
          {!showDisable ? (
            <div>
              <button onClick={() => setShowDisable(true)} className="btn-ghost text-sm text-terracotta-600" data-testid="mfa-disable-btn">
                <ShieldOff className="h-4 w-4" /> {t("profile.disableMfa")}
              </button>
            </div>
          ) : (
            <form onSubmit={disable} className="flex flex-wrap items-center gap-2">
              <input type="password" required value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
                placeholder={t("profile.confirmYourPassword")} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="mfa-disable-password" />
              <button disabled={busy} type="submit" className="btn-accent text-sm" data-testid="mfa-disable-confirm">
                {busy ? t("profile.disabling") : t("profile.confirmDisable")}
              </button>
              <button type="button" onClick={() => setShowDisable(false)} className="text-xs text-muted-foreground hover:underline">{t("halaqa.cancel")}</button>
            </form>
          )}
        </div>
      ) : setup ? (
        <div className="space-y-4">
          <p className="text-sm text-foreground/80">{t("profile.scanThisQrCodeWith")}</p>
          <img src={setup.qr_code} alt={t("profile.mfaQrCode")} className="h-44 w-44 rounded-xl border border-sand-300" data-testid="mfa-qr-code" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{t("profile.canTScanEnterManually")}</span>
            <code className="px-2 py-1 rounded bg-sand-200/60 font-semibold">{setup.secret}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success(t("achievements.copied")); }} className="hover:text-forest-700">
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <form onSubmit={confirmSetup} className="flex flex-wrap items-center gap-2">
            <input
              required maxLength={6} pattern="\d{6}"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="rounded-xl border border-sand-300 px-4 py-2 text-center text-xl tracking-[8px] font-display"
              data-testid="mfa-setup-code-input"
            />
            <button disabled={busy || code.length !== 6} type="submit" className="btn-primary text-sm" data-testid="mfa-setup-confirm">
              {busy ? t("profile.verifying") : t("profile.enableMfa")}
            </button>
            <button type="button" onClick={() => { setSetup(null); setCode(""); }} className="text-xs text-muted-foreground hover:underline">{t("halaqa.cancel")}</button>
          </form>
        </div>
      ) : (
        <button onClick={startSetup} disabled={busy} className="btn-primary text-sm disabled:opacity-60" data-testid="mfa-start-setup">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} {t("profile.setUpTwoFactor")}
        </button>
      )}
    </div>
  );
}

function CollapsibleSection({ icon: Icon, label, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button" onClick={() => setOpen((o) => !o)}
        className="w-full hifz-card hifz-card-hover flex items-center justify-between gap-3 text-left"
        data-testid={testid}
      >
        <span className="font-display font-semibold text-lg text-forest-700 inline-flex items-center gap-2">
          <Icon className="h-4.5 w-4.5" /> {label}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

export default function Profile() {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profile-page">
      <section className="hifz-card grain bg-forest-700 border-forest-700 text-sand-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-gold-500 text-foreground grid place-items-center font-display font-bold text-xl">
              {(user?.name || "U")[0].toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-display font-semibold text-2xl">{user?.name}</div>
            <div className="text-sm text-sand-100/70"><span className="capitalize">{user?.role}</span> · {user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-center">
          <div><div className="font-display font-bold text-2xl text-gold-400">{user?.streak ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-sand-100/60">{t("profile.streak")}</div></div>
          <div><div className="font-display font-bold text-2xl text-gold-400">{user?.total_memorized ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-sand-100/60">{t("profile.ayahs")}</div></div>
          <div><div className="font-display font-bold text-2xl text-gold-400">{user?.xp ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-sand-100/60">{t("profile.xp")}</div></div>
          <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost text-xs border-sand-100/30 text-sand-100 hover:bg-sand-100/10" data-testid="profile-logout-btn">
            <LogOut className="h-3.5 w-3.5" /> {t("profile.signOut")}
          </button>
        </div>
      </section>

      {/* Streak Recovery — moved here from the home page */}
      <StreakRecoveryCard />

      <CollapsibleSection icon={User} label={t("profile.yourInformation")} testid="profile-info-toggle">
        <ProfileInfoForm />
      </CollapsibleSection>
      <CollapsibleSection icon={Lock} label={t("profile.changePassword")} testid="password-toggle">
        <ChangePasswordForm />
      </CollapsibleSection>
      <MfaSection />

      <section className="hifz-card space-y-2" data-testid="profile-delete-account">
        <h2 className="font-display font-semibold text-lg text-terracotta-600">{t("profile.deleteAccount")}</h2>
        <p className="text-sm text-muted-foreground">{t("profile.deleteAccountBody")}</p>
        <button type="button" onClick={goToDeleteAccount} className="btn-ghost text-sm text-terracotta-600" data-testid="profile-delete-account-btn">
          {t("profile.deleteAccountButton")}
        </button>
      </section>

      <HubPage
        eyebrow="Profile"
        title={t("profile.yourJourneyYourSettings")}
        testid="profile-hub"
        compact
        tiles={[
          { to: "/app/revision", icon: Target, label: "Daily Goals", desc: "Adjust your pace anytime — ayahs, pages or minutes.", testid: "profile-tile-goals" },
          { to: "/app/progress", icon: BarChart3, label: "My Progress", desc: "Timeline of everything you've memorized and revised.", testid: "profile-tile-progress" },
          { to: "/app/achievements", icon: Award, label: "Achievements & Invites", desc: "Your badges, level, and friends you've invited.", testid: "profile-tile-achievements" },
          { to: "/app/pulse", icon: Activity, label: "AI Insights", desc: "Your weekly pulse — trends, accuracy and coach notes.", testid: "profile-tile-insights" },
          { to: "/app/submissions", icon: Inbox, label: "My Submissions", desc: "Recitations you've sent to your teacher.", testid: "profile-tile-submissions" },
        ]}
      />
    </div>
  );
}
